import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Select,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { db, storage } from "./firebase";
import {
  collection,
  addDoc,
  Timestamp,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import dayjs from "dayjs";
import UploadPreview from "./components/UploadPreview";

export default function BurhanExpenses() {
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [receipt, setReceipt] = useState(null);
  const [receiptName, setReceiptName] = useState("");

  const [expenses, setExpenses] = useState([]);
  const [selectedDay, setSelectedDay] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const [openReceiptViewer, setOpenReceiptViewer] = useState(false);
  const [viewerImage, setViewerImage] = useState("");

  const username = localStorage.getItem("username");
  const employeeName = username === "art@1234" ? "برهان" : "—";

  const expenseTypes = [
    "تكسي",
    "اجرة عامل",
    "اجرة نقل لوحات",
    "بنزين",
    "صيانة سيارة",
    "قرطاسية",
    "معدات",
    "أخرى",
  ];

  // تحميل جميع مصاريف برهان
  useEffect(() => {
    const q = collection(db, "expenses");
    const unsub = onSnapshot(q, (snap) => {
      let data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => d.employee === employeeName);

      if (selectedDay) {
        data = data.filter((exp) =>
          dayjs(exp.date.toDate()).isSame(selectedDay, "day")
        );
      }

      setExpenses(data);
    });

    return () => unsub();
  }, [selectedDay]);

  // تنسيق المبلغ
  const formatAmount = (value) => {
    if (!value) return "";
    const num = Number(value.replace(/,/g, ""));
    return isNaN(num) ? "" : num.toLocaleString("en-US");
  };

  const formatIQD = (value) => Number(value).toLocaleString("en-US") + " دينار";

  // إضافة المصروف
  const handleAddExpense = async () => {
    if (!type || !amount) {
      alert("يرجى ملء نوع المصروف والمبلغ");
      return;
    }

    setIsLoading(true);
    let uploadedUrl = "";

    if (receipt) {
      const storageRef = ref(storage, `expenses/${Date.now()}-${receipt.name}`);
      await uploadBytes(storageRef, receipt);
      uploadedUrl = await getDownloadURL(storageRef);
    }

    await addDoc(collection(db, "expenses"), {
      employee: employeeName,
      type,
      description,
      date: Timestamp.fromDate(new Date(date)),
      amount: Number(amount.replace(/,/g, "")),
      receiptUrl: uploadedUrl,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    setType("");
    setDescription("");
    setAmount("");
    setReceipt(null);
    setReceiptName("");
    setIsLoading(false);
  };

  // فتح نافذة تعديل
  const openEdit = (exp) => {
    setEditingExpense(exp);
    setEditOpen(true);
  };

  // حفظ التعديلات
  const handleEditSave = async () => {
    const convertedDate =
      editingExpense.date?.toDate
        ? editingExpense.date.toDate()
        : new Date(editingExpense.date);

    await updateDoc(doc(db, "expenses", editingExpense.id), {
      type: editingExpense.type,
      description: editingExpense.description,
      amount: Number(editingExpense.amount.toString().replace(/,/g, "")),
      date: Timestamp.fromDate(convertedDate),
      updatedAt: Timestamp.now(),
    });

    setEditOpen(false);
  };

  // حذف المصروف بالكامل
  const handleDelete = async (id) => {
    const ok = window.confirm("⚠ هل تريد حذف هذا المصروف نهائيًا؟");
    if (!ok) return;

    await deleteDoc(doc(db, "expenses", id));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
        onClick={() => window.history.back()}
      >
        رجوع
      </Button>

      {/* إضافة مصروف */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          💵 إضافة مصروف جديد
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
         <FormControl fullWidth sx={{ mb: 2 }}>
  <InputLabel sx={{ fontSize: "18px" }}>نوع المصروف</InputLabel>
  <Select
    fullWidth
    value={type}
    onChange={(e) => setType(e.target.value)}
    label="نوع المصروف"
    displayEmpty
    sx={{
      height: "55px !important",
      fontSize: "18px",
      "& .MuiSelect-select": {
        height: "55px",
        display: "flex",
        alignItems: "center",
      },
    }}
    MenuProps={{
      PaperProps: {
        sx: { maxHeight: 300, fontSize: "18px" },
      },
    }}
  >
    <MenuItem disabled value="">
      اختر نوع المصروف
    </MenuItem>

    {expenseTypes.map((t) => (
      <MenuItem key={t} value={t} sx={{ fontSize: "18px" }}>
        {t}
      </MenuItem>
    ))}
  </Select>
</FormControl>

          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="المبلغ"
              value={amount}
              onChange={(e) => setAmount(formatAmount(e.target.value))}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="date"
              label="التاريخ"
              InputLabelProps={{ shrink: true }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="الوصف (اختياري)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          {/* رفع الوصل */}
          <Grid item xs={12} md={6}>
            <Button component="label" variant="outlined" fullWidth>
              📤 رفع الوصل / الفاتورة
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files[0];
                  e.target.value = "";
                  setReceipt(f);
                  setReceiptName(f?.name || "");
                }}
              />
            </Button>

            <UploadPreview
              files={receipt ? [receipt] : []}
              onChange={(list) => {
                setReceipt(list[0] || null);
                setReceiptName(list[0]?.name || "");
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddExpense}
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              {isLoading ? "جاري الحفظ..." : "إضافة المصروف"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* عرض المصاريف */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          🗂️ مصاريف الشهر
        </Typography>

        <TextField
          type="date"
          fullWidth
          label="اختيار اليوم"
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 3 }}
          value={selectedDay}
          onChange={(e) => setSelectedDay(e.target.value)}
        />

        <Grid container spacing={3}>
          {expenses.map((exp) => (
            <Grid item xs={12} sm={6} md={4} key={exp.id}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600}>
                    {exp.type}
                  </Typography>

                  <Typography color="text.secondary" sx={{ mb: 1 }}>
                    {dayjs(exp.date.toDate()).format("YYYY-MM-DD")}
                  </Typography>

                  <Typography fontWeight={700} color="primary">
                    {formatIQD(exp.amount)}
                  </Typography>

                  {exp.receiptUrl && (
                    <Button
                      variant="text"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setViewerImage(exp.receiptUrl);
                        setOpenReceiptViewer(true);
                      }}
                    >
                      📎 عرض الوصل
                    </Button>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => openEdit(exp)}
                  >
                    تعديل
                  </Button>

                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(exp.id)}
                  >
                    حذف
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Popup عرض الوصل */}
      <Dialog
        open={openReceiptViewer}
        onClose={() => setOpenReceiptViewer(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          عرض الوصل
          <IconButton
            sx={{ position: "absolute", right: 10, top: 10 }}
            onClick={() => setOpenReceiptViewer(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <img
            src={viewerImage}
            alt="receipt"
            style={{ width: "100%", borderRadius: "8px" }}
          />
        </DialogContent>
      </Dialog>

      {/* Popup التعديل */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>
          تعديل المصروف
          <IconButton
            sx={{ position: "absolute", right: 10, top: 10 }}
            onClick={() => setEditOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* نوع المصروف */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ fontSize: "18px" }}>نوع المصروف</InputLabel>
            <Select
              fullWidth
              value={editingExpense?.type || ""}
              onChange={(e) =>
                setEditingExpense((p) => ({ ...p, type: e.target.value }))
              }
              sx={{
                fontSize: "18px",
                height: "55px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {expenseTypes.map((t) => (
                <MenuItem
                  key={t}
                  value={t}
                  sx={{ fontSize: "18px", paddingY: "12px" }}
                >
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            sx={{ mb: 2 }}
            label="الوصف"
            value={editingExpense?.description || ""}
            onChange={(e) =>
              setEditingExpense((p) => ({ ...p, description: e.target.value }))
            }
          />

          <TextField
            fullWidth
            sx={{ mb: 2 }}
            label="المبلغ"
            value={editingExpense?.amount || ""}
            onChange={(e) =>
              setEditingExpense((p) => ({
                ...p,
                amount: formatAmount(e.target.value),
              }))
            }
          />

          <TextField
            fullWidth
            type="date"
            label="التاريخ"
            InputLabelProps={{ shrink: true }}
            value={
              editingExpense
                ? dayjs(
                    editingExpense.date?.toDate
                      ? editingExpense.date.toDate()
                      : editingExpense.date
                  ).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              setEditingExpense((p) => ({ ...p, date: e.target.value }))
            }
          />
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleEditSave}>
            حفظ التعديلات
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
